import React, { useEffect, useMemo, useRef, useState } from "react";

import CellSaveStatusIndicator from "./CellSaveStatusIndicator";
// @ts-expect-error The existing JavaScript select wrapper has no type declarations.
import SelectMenu from "./SelectMenu";
import type { CellSaveStatus } from "../../hooks/useCellSaveState";
import type { SelectOption, SelectValue } from "./selectTypes";

type Selection<Option extends SelectOption> = Option | Option[] | null;
type InlineSelectCellProps<Option extends SelectOption> = {
  ariaLabel?: string;
  disabled?: boolean;
  disableWhileSaving?: boolean;
  isClearable?: boolean;
  name?: string;
  options: Option[];
  saveStatus?: CellSaveStatus;
  parentClass?: string;
  menuPortalTarget?: HTMLElement | null;
  placeholder?: string;
} & (
  | { isMulti: true; value?: Option["value"][] | null; onCommit: (selection: Option[]) => unknown }
  | { isMulti?: false; value?: Option["value"] | null; onCommit: (selection: Option | null) => unknown }
);

const getSelectionSignature = <Option extends SelectOption,>(selection: Selection<Option> | SelectValue | SelectValue[] | undefined) => {
  const getValue = (item: Option | SelectValue | null | undefined) =>
    String(item && typeof item === "object" ? item.value : (item ?? ""));

  return Array.isArray(selection)
    ? selection.map(getValue).sort().join("|")
    : getValue(selection);
};

/**
 * Inline select that immediately commits changes while retaining the user's
 * selected value until refreshed server data arrives.
 */
export default function InlineSelectCell<Option extends SelectOption>(props: InlineSelectCellProps<Option>) {
  const {
    ariaLabel,
    disabled = false,
    disableWhileSaving = true,
    isClearable = false,
    isMulti = false,
    name,
    options,
    saveStatus = "idle",
    value,
    parentClass = "editable-table-select",
    menuPortalTarget,
    placeholder,
  } = props;
  const valueSignature = getSelectionSignature(value);
  const selectedOption = useMemo(() => {
    if (props.isMulti) {
      const selectedValues = new Set((props.value ?? []).map(String));
      return options.filter((option) =>
        selectedValues.has(String(option.value)),
      );
    }

    return options.find((option) => option.value === value) ?? null;
  }, [props.isMulti, props.value, options, value]);
  const [draftOption, setDraftOption] = useState<Selection<Option>>(selectedOption);
  const previousValueSignature = useRef(valueSignature);
  const pendingValueSignature = useRef<string | null>(null);
  const draftSignature = getSelectionSignature(draftOption);

  useEffect(() => {
    if (pendingValueSignature.current !== null) {
      // Only the newest committed value can confirm the draft. Ignore older
      // or out-of-order parent refreshes regardless of save-indicator timing.
      if (pendingValueSignature.current === valueSignature) {
        pendingValueSignature.current = null;
        previousValueSignature.current = valueSignature;
      }
      return;
    }

    if (previousValueSignature.current === valueSignature) return;

    previousValueSignature.current = valueSignature;
    setDraftOption(selectedOption);
  }, [selectedOption, valueSignature]);

  const isSaving = saveStatus === "saving";

  return (
    <div className="flex min-w-0 items-center gap-1">
      <SelectMenu
        handleInputChange={(event: { target: { value: Selection<Option> } }) => {
          const nextOption = isMulti ? (event.target.value ?? []) : event.target.value;
          const nextSignature = getSelectionSignature(nextOption);

          if (nextSignature === draftSignature) return;

          // Keep the new selection visible while the parent refreshes its row.
          pendingValueSignature.current = nextSignature;
          setDraftOption(nextOption);
          // SelectMenu's legacy event follows the configured selection mode.
          if (props.isMulti) props.onCommit(nextOption as Option[]);
          else props.onCommit(nextOption as Option | null);
        }}
        name={name}
        options={options}
        value={draftOption}
        isMulti={isMulti}
        isClearable={isClearable}
        placeholder={placeholder}
        parentClass={parentClass}
        ariaLabel={ariaLabel}
        isDisabled={disabled || (disableWhileSaving && isSaving)}
        menuPortalTarget={
          menuPortalTarget ??
          (typeof document !== "undefined" ? document.body : undefined)
        }
      />
      {!disabled ? <CellSaveStatusIndicator status={saveStatus} /> : null}
    </div>
  );
}
